/** Meeting-info card (Meet/Zoom-style) on the session page — surfaces the
 * pending participants' join links, copyable, so the operator can share
 * them ahead of time instead of digging into the invite modal.
 *
 * Always visible on a live/pending session: when nobody has been invited
 * yet it shows an empty state + an "Invite field user" CTA, since a join
 * link is minted per invited participant (there's no single session-wide
 * link — each field user gets a unique tokenised URL + OTP). */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { fetchSessionParticipants } from "@/modules/dsr";

export function SessionJoinLinkCard({
  sessionId,
  onInvite,
  canInviteFieldUser = true,
}: {
  sessionId: string;
  onInvite?: () => void;
  /** Whether inviting a field user makes sense here.
   *
   * False on a campaign session: the beneficiary IS the field user, so
   * offering to invite one reads as though they were missing, and
   * offering a SECOND one is noise — nobody runs an inspection of one
   * beneficiary's site with two field participants. True on a
   * standalone session, where there is no beneficiary to derive the
   * field user from and somebody has to be invited.
   *
   * Reported 2026-09-24. */
  canInviteFieldUser?: boolean;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState<string | null>(null);
  const [copyFailed, setCopyFailed] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ["session-participants", sessionId, "with-pending"],
    queryFn: () => fetchSessionParticipants(sessionId, { includePending: true }),
    staleTime: 4000,
  });

  const rows = q.data ?? [];
  const links = rows.filter((p) => p.pending && p.join_url);
  // Reported bug (2026-09-08): once the field participant actually
  // joined, `links` correctly empties out (nothing pending anymore),
  // but the empty-state copy still said "nobody invited yet" — which
  // is wrong and confusing while someone IS live on the call. Track
  // joined-and-present participants separately so the two empty
  // states ("truly nobody" vs "already connected, nothing pending")
  // read differently.
  // Every field user who has ever joined, whether or not they are still
  // here. Reported 2026-09-23: "the beneficiary has been added and also
  // joined, why does it say no one has been invited?"
  //
  // This list used to require `!left_at`. That was invisible while
  // `left_at` was never written — a joined participant always matched.
  // Once the presence webhook started stamping it (#235), anyone who
  // dropped fell OUT of the list, so the card showed the "nobody has
  // been invited yet" empty state and offered no re-issue button — for
  // precisely the person who needs one. Keying the re-issue on "still
  // present" was backwards: someone who LEFT is the one who needs a
  // fresh link.
  const fieldUsers = rows.filter((p) => !p.pending && p.role === "field");
  const presentField = fieldUsers.filter((p) => !p.left_at);
  const departedField = fieldUsers.filter((p) => !!p.left_at);

  // Field users whose original invite hasn't expired yet. An expired one
  // must NOT be advertised as working — that would send the operator
  // down a second dead end.
  const now = Date.now();
  const stillValid = fieldUsers.filter((p) => {
    const exp = p.invite_expires_at ? Date.parse(p.invite_expires_at) : NaN;
    return !Number.isNaN(exp) && exp > now;
  });

  // Reported bug (2026-09-17): "the copy link did not work, not sure
  // where" — `navigator.clipboard.writeText` silently rejects outside a
  // secure/focused context (and is undefined entirely in some embedded
  // views), and the old handler swallowed that failure with `.catch(()
  // => undefined)` — the button just did nothing, with zero indication
  // it had failed. Now: fall back to the legacy `execCommand("copy")`
  // via the visible input field, and if BOTH paths fail, say so instead
  // of failing silently.
  const inputRefs = useRef(new Map<string, HTMLInputElement>());

  // Reported 2026-09-23: "the client can't connect, I have no link to
  // copy". A field user who joined once and then dropped left the
  // operator with nothing — the delivery token is erased on redemption,
  // and every re-send surface is gated on a still-pending invite. So
  // this branch used to be a dead end: a sentence saying someone is
  // connected, and a button to invite a DIFFERENT person. Now each
  // joined field participant can be handed a freshly minted link.
  const [reissued, setReissued] = useState<Record<string, string>>({});
  const reissue = useMutation({
    mutationFn: async (participantId: string) => {
      const res = await api.post<{ join_url: string }>(
        ApiPaths.sessionParticipantReissueInvite(sessionId, participantId),
        {},
      );
      return { participantId, joinUrl: res.data.join_url };
    },
    onSuccess: ({ participantId, joinUrl }) =>
      setReissued((prev) => ({ ...prev, [participantId]: joinUrl })),
  });

  const linkRow = (id: string, label: string, url: string) => (
    <div key={id} className="space-y-1">
      <div className="text-[11px] text-nb-text-2">{label}</div>
      <div className="flex items-center gap-1.5">
        <input
          ref={(el) => {
            if (el) inputRefs.current.set(id, el);
            else inputRefs.current.delete(id);
          }}
          readOnly
          value={url}
          onFocus={(e) => e.currentTarget.select()}
          className="input h-7 flex-1 py-0 text-[11px]"
          aria-label={t("session.joinInfo")}
        />
        <button
          type="button"
          className="btn-ghost shrink-0 text-xs"
          onClick={() => copy(url, id)}
        >
          {copied === id ? t("common.copied") : t("common.copy")}
        </button>
      </div>
      {copyFailed === id && (
        <p className="text-[10px] text-cvp-danger">
          {t(
            "session.joinInfoCopyFailed",
            "Could not copy automatically — the link is selected above, press Ctrl/Cmd+C.",
          )}
        </p>
      )}
    </div>
  );

  const legacyCopy = (id: string): boolean => {
    const input = inputRefs.current.get(id);
    if (!input) return false;
    input.select();
    try {
      return document.execCommand("copy");
    } catch {
      return false;
    }
  };

  const copy = (url: string, id: string) => {
    setCopyFailed(null);
    const onOk = () => {
      setCopied(id);
      setTimeout(() => setCopied(null), 1500);
    };
    const onFail = () => {
      if (legacyCopy(id)) {
        onOk();
      } else {
        setCopyFailed(id);
        setTimeout(() => setCopyFailed(null), 4000);
      }
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(onOk).catch(onFail);
    } else {
      onFail();
    }
  };

  return (
    <div className="space-y-2 rounded-md border border-nb-mint/30 bg-nb-mint/5 p-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-nb-mint">
        {t("session.joinInfo")}
      </h3>
      <p className="text-xs text-nb-text-3">{t("session.joinInfoHint")}</p>

      {links.length === 0 ? (
        <div className="space-y-2">
          <p className="text-xs text-nb-text-2">
            {presentField.length > 0
              ? t("session.joinInfoFieldConnected")
              : departedField.length > 0
                ? t(
                    "session.joinInfoFieldLeft",
                    "The field participant joined and has since left the call.",
                  )
                : t("session.joinInfoEmpty")}
          </p>

          {/* Their EXISTING link usually still works, and nobody could
              tell: clearing `delivery_token` on redemption erases our
              copy of the URL, not the credential. Saying so is better
              than minting a second link the recipient doesn't have —
              "check your SMS" solves it without sending anything. */}
          {stillValid.length > 0 && (
            <p className="text-xs text-nb-text-2">
              {t("session.originalLinkStillValid", {
                expires: formatDateTime(stillValid[0]?.invite_expires_at),
                defaultValue:
                  "Their original link still works until {{expires}} — ask them to reopen it from their email or SMS.",
              })}
            </p>
          )}

          {/* A participant the system believes is connected may well have
              dropped — presence is only as fresh as the last LiveKit
              event. Either way the operator needs a link they can hand
              over, so offer one per joined field participant. */}
          {fieldUsers.map((p) =>
            reissued[p.id] ? (
              linkRow(p.id, p.display_name || p.role, reissued[p.id] ?? "")
            ) : (
              <button
                key={p.id}
                type="button"
                className="btn-ghost w-full text-xs"
                disabled={reissue.isPending}
                onClick={() => reissue.mutate(p.id)}
              >
                {reissue.isPending && reissue.variables === p.id
                  ? t("common.loading")
                  : t("session.reissueJoinLink", {
                      name: p.display_name || p.role,
                      defaultValue: "New link for {{name}}",
                    })}
              </button>
            ),
          )}
          {reissue.isError && (
            <p className="text-[10px] text-cvp-danger">
              {t(
                "session.reissueJoinLinkFailed",
                "Could not create a new link.",
              )}
            </p>
          )}

          {/* On a campaign session the beneficiary is the field user, so
              this is hidden once one exists. It stays when there is
              genuinely nobody — a campaign session whose invite was
              never issued would otherwise strand the operator with no
              action at all. */}
          {onInvite && (canInviteFieldUser || fieldUsers.length === 0) && (
            <button
              type="button"
              className="btn-primary w-full text-xs"
              onClick={onInvite}
            >
              {fieldUsers.length > 0
                ? t("session.inviteAnotherParticipant")
                : t("session.inviteFieldUser")}
            </button>
          )}
        </div>
      ) : (
        <>
          {links.map((p) =>
            linkRow(p.id, p.display_name || p.role, p.join_url ?? ""),
          )}
          {/* Adding a SECOND field user. Never offered on a campaign
              session — the beneficiary is already the field user, and
              nobody inspects one beneficiary's site with two field
              participants. Standalone sessions have no beneficiary to
              derive one from, so it stays there. */}
          {onInvite && canInviteFieldUser && (
            <button
              type="button"
              className="btn-ghost w-full text-xs"
              onClick={onInvite}
            >
              + {t("session.inviteAnotherFieldUser", "Invite another field user")}
            </button>
          )}
        </>
      )}
    </div>
  );
}
